import { dispatcher } from 'dispatcher';
import { useCallback, useEffect } from 'react';
import { I18N_REDUCER } from 'js/store/constants';
import { useCustomSelector } from 'js/store/hooks';
import { useSelector, useDispatch } from 'react-redux';
import { length, values, prop, path, pick } from 'ramda';

import * as helpers from './helpers';
import * as constants from './constants';
import * as actions from './store/actions';

export const useTranslations = (prefix = '') => {
    const i18n = useCustomSelector(I18N_REDUCER);

    return useCallback(
        /**
         * @example:
         * const t = useTranslations();
         * t('playtech.mobile.system.tk_prefix.my.tk');
         * t(['playtech.mobile.system.tk_prefix.my.tk', 'playtech.mobile.system.tk_prefix.my.tk2']);

         * const t = useTranslations('playtech.mobile.system.tk_prefix.');
         * t('my.tk');
         * t(['my.tk', 'my.tk2']);

         * when playtech.mobile.system.tk_prefix.my.tk value is not set
         * const t = useTranslations('playtech.mobile.system.tk_prefix.');
         * t('my.tk', 'default value');

         * when playtech.mobile.system.tk_prefix.my.tk value contains {0} {1} ...
         * const t = useTranslations('playtech.mobile.system.tk_prefix.');
         * t('my.tk', [replaceStr1, replaceStr2]);

         * const t = useTranslations('playtech.mobile.system.tk_prefix.');
         * t('my.tk', [replaceStr1, replaceStr2], 'default value');
         *
         * @param {String|string[]} translationKey Translation Key - whole or end part of it
         * @param {...*} [args] single {String} defaultValue
         *                      or Array<String> of replacers followed by {String} defaultValue
         * @returns {String}
         */
        (translationKey, ...args) => {
            if (Array.isArray(translationKey)) {
                const filteredTranslationKey = translationKey.filter(Boolean);

                for (const tk of filteredTranslationKey) {
                    const tkWithPrefix = tk.startsWith(constants.TK_PREFIX) ? tk : prefix + tk;
                    const tkValue = helpers.getTranslationKeyValue({ translationKey: tkWithPrefix, prefix, args, i18n });

                    if (tkValue !== tkWithPrefix || tk === filteredTranslationKey[filteredTranslationKey.length - 1]) {
                        return tkValue;
                    }
                }
            }

            return helpers.getTranslationKeyValue({ translationKey, prefix, args, i18n });
        },
        [i18n, prefix]
    );
};

export const useSmartTranslations = () => {
    const translate = useTranslations();

    return (tk, ...args) => tk.startsWith(constants.TK_PREFIX) ? translate(tk, ...args) : tk;
};

export const useInitTranslations = (translations) => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(actions.setDefaultTranslationsAction(translations));
    }, [dispatch, translations]);
};

export const useGetAddTranslations = () => {
    const i18nDispatch = useDispatch();

    return useCallback(({ translations, isPageTranslations, isPageInPopupTranslations }) => {
        if (isPageTranslations && !isPageInPopupTranslations) {
            dispatcher.dispatch('i18n:page:translations', translations);
        } else if (isPageInPopupTranslations) {
            dispatcher.dispatch('i18n:pageInPopup:translations', translations);
        } else {
            dispatcher.dispatch('i18n:system:translations', translations);
        }

        i18nDispatch(actions.addTranslationsAction(translations));
    }, [i18nDispatch]);
};

export const useTranslationsService = () => {
    const { translations, defaultTranslations } = useCustomSelector(I18N_REDUCER, pick(['translations', 'defaultTranslations']));
    const i18nDispatch = useDispatch();
    const params = new URLSearchParams(location.search);
    const showTrKeysParam = params.get('showTranslationKeys');
    const showTranslationKeys = ['1', '2'].includes(showTrKeysParam);

    useEffect(() => {
        if (showTranslationKeys) {
            i18nDispatch(actions.showTranslationKeysAction(true));
        }
    }, [showTranslationKeys, i18nDispatch]);

    useEffect(() => {
        if (showTranslationKeys) {
            helpers.validateTranslations({ translations, defaultTranslations });
        }
    }, [translations, defaultTranslations, showTranslationKeys]);
};

export const useTranslationsLoaded = () => {
    const translations = useCustomSelector(I18N_REDUCER, prop('translations'));

    return length(values(translations)) > 0;
};

export const useIsShowTranslationKeys = () => useSelector(path([I18N_REDUCER, 'showTranslationKeys']));
